import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchReleaseClients, updateReleaseStatus, fetchRelease, deleteRelease } from "../api/releasesApi";
import { fetchReleaseFeatures, deleteFeatureFromRelease, fetchFeatureClients } from "../api/featuresApi";
import { fetchAllLiveOrgs } from "../api/usersApi";
import { getClientDisplayName } from "../utils/clientDisplayName";
import AddFeatureModal from "./AddFeatureModal";
import ExecuteReleaseModal from "./ExecuteReleaseModal";
import "./ReleaseDetailPanel.css";

/* ─── Helpers ──────────────────────────────────────────────────────────────── */

function formatDateTime(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("en-IN", {
        year: "numeric", month: "short", day: "numeric",
        hour: "2-digit", minute: "2-digit",
    });
}

function StatusBadge({ status }) {
    const s = (status || "").toUpperCase();
    const cls =
        s === "RELEASED" ? "rdp-status-badge rdp-status-badge--released"
            : s === "READY" ? "rdp-status-badge rdp-status-badge--ready"
                : "rdp-status-badge rdp-status-badge--draft";
    return <span className={cls}>{status}</span>;
}

const STEPS = ["DRAFT", "READY", "RELEASED"];

function StatusJourney({ status }) {
    const current = STEPS.indexOf((status || "").toUpperCase());
    return (
        <div className="rdp-journey" aria-label="Release status journey">
            {STEPS.map((step, i) => {
                const done = i < current;
                const active = i === current;
                return (
                    <React.Fragment key={step}>
                        <div className={`rdp-journey__step ${active ? "rdp-journey__step--active" : ""} ${done ? "rdp-journey__step--done" : ""}`}>
                            <div className="rdp-journey__dot" aria-hidden="true">
                                {done ? (
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                                        stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                ) : null}
                            </div>
                            <span className="rdp-journey__label">{step}</span>
                        </div>
                        {i < STEPS.length - 1 && (
                            <div className={`rdp-journey__line ${done ? "rdp-journey__line--done" : ""}`} aria-hidden="true" />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
}

/* ─── Clients reached (API 6) ─────────────────────────────── */

function ClientsReached({ releaseId, features: featuresProp }) {
    const loginDetails = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem("loginDetails") || "null");
        } catch {
            return null;
        }
    }, []);

    const [liveClientDisplayMap, setLiveClientDisplayMap] = useState(() => new Map());
    const liveMapFetchedRef = useRef(false);

    // API 6 — total clients for the whole release
    const { data, isLoading, isError, error } = useQuery({
        queryKey: ["releaseClients", releaseId],
        queryFn: () => fetchReleaseClients(releaseId),
        enabled: !!releaseId,
    });

    const totalClientIds = data?.data?.clientIds || [];
    const totalCount = data?.data?.count ?? totalClientIds.length;
    const safeFeatures = Array.isArray(featuresProp) ? featuresProp : [];

    // Per-feature client data (API 8)
    const [featureClientMap, setFeatureClientMap] = useState({});
    const [featureLoading, setFeatureLoading] = useState({});
    const [featureErrors, setFeatureErrors] = useState({});

    // UI state
    const [expandedFeatures, setExpandedFeatures] = useState({});
    const [searchTerm, setSearchTerm] = useState("");

    // Load clients per feature when features are available
    useEffect(() => {
        if (safeFeatures.length === 0) return;

        safeFeatures.forEach((feature) => {
            const fId = feature.featureId;
            if (!fId) return;
            // Skip if already loaded or loading
            if (featureClientMap[fId] || featureLoading[fId]) return;

            setFeatureLoading((prev) => ({ ...prev, [fId]: true }));

            fetchFeatureClients(fId, 100) // API max limit is 100
                .then((res) => {
                    const clients = res?.data || [];
                    setFeatureClientMap((prev) => ({ ...prev, [fId]: clients }));
                })
                .catch((err) => {
                    setFeatureErrors((prev) => ({ ...prev, [fId]: err?.message || "Failed to load" }));
                })
                .finally(() => {
                    setFeatureLoading((prev) => ({ ...prev, [fId]: false }));
                });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [safeFeatures]);

    // Live users fallback: resolve clientId → display name when not in loginDetails.clientData
    useEffect(() => {
        if (!releaseId || liveMapFetchedRef.current) return;
        liveMapFetchedRef.current = true;
        fetchAllLiveOrgs()
            .then((clients) => {
                const map = new Map();
                for (const c of clients || []) {
                    if (c.clientId) {
                        const name = (c.displayName || c.org || "").trim();
                        if (name) map.set(c.clientId, name);
                    }
                }
                setLiveClientDisplayMap(map);
            })
            .catch(() => { liveMapFetchedRef.current = false; });
    }, [releaseId]);

    const resolveClientDisplayName = useCallback((clientId) => {
        const fromLogin = getClientDisplayName(loginDetails, clientId);
        if (fromLogin && fromLogin !== clientId) return fromLogin;
        return liveClientDisplayMap.get(clientId) || clientId;
    }, [loginDetails, liveClientDisplayMap]);

    const toggleExpand = (featureId) => {
        setExpandedFeatures((prev) => ({ ...prev, [featureId]: !prev[featureId] }));
    };

    const expandAll = () => {
        const all = {};
        safeFeatures.forEach((f) => { all[f.featureId] = true; });
        setExpandedFeatures(all);
    };

    const collapseAll = () => setExpandedFeatures({});

    const allExpanded = safeFeatures.length > 0 && safeFeatures.every((f) => expandedFeatures[f.featureId]);

    // Search filter (by clientId and display name)
    const filterClients = (clients) => {
        if (!searchTerm.trim()) return clients;
        const q = searchTerm.toLowerCase();
        return clients.filter((c) => {
            const id = (c.clientId || "").toLowerCase();
            const name = resolveClientDisplayName(c.clientId).toLowerCase();
            return id.includes(q) || name.includes(q);
        });
    };

    // Loading state
    if (isLoading) {
        return (
            <div className="rdp-action-card rdp-action-card--released">
                <div className="rdp-action-card__header">
                    <span className="rdp-action-card__icon" aria-hidden="true">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                            <circle cx="9" cy="7" r="4" />
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                        </svg>
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <h3 className="rdp-action-card__title">Clients Reached</h3>
                        <p className="rdp-action-card__desc">Loading client data…</p>
                    </div>
                </div>
                <div className="rdp-clients-skeleton">
                    {[1, 2, 3, 4, 5].map(i => <span key={i} className="rdp-client-chip rdp-client-chip--skeleton" />)}
                </div>
            </div>
        );
    }

    // Error state
    if (isError) {
        return (
            <div className="rdp-action-card rdp-action-card--released">
                <div className="rdp-action-card__header">
                    <span className="rdp-action-card__icon" aria-hidden="true">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                            <circle cx="9" cy="7" r="4" />
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                        </svg>
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <h3 className="rdp-action-card__title">Clients Reached</h3>
                        <p className="rdp-action-card__desc rdp-action-card__desc--error">
                            {error?.message || "Failed to load client list."}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // No features available — fallback to flat view
    const showGrouped = safeFeatures.length > 0;

    return (
        <div className="rdp-action-card rdp-action-card--released">
            {/* ── Header ─────────────────────────────────────────── */}
            <div className="rdp-action-card__header">
                <span className="rdp-action-card__icon" aria-hidden="true">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                        <circle cx="9" cy="7" r="4" />
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 className="rdp-action-card__title">Clients Reached</h3>
                    <p className="rdp-action-card__desc">
                        {totalCount > 0
                            ? safeFeatures.length === 0
                                ? <><strong>{totalCount}</strong> client{totalCount !== 1 ? "s" : ""} received this release.</>
                                : <><strong>{totalCount}</strong> client{totalCount !== 1 ? "s" : ""} received this release across <strong>{safeFeatures.length}</strong> feature{safeFeatures.length !== 1 ? "s" : ""}</>
                            : "No clients received this release yet."}
                    </p>
                </div>
            </div>

            {/* ── Search + expand controls ────────────────────────── */}
            {showGrouped && totalCount > 0 && (
                <div className="rdp-cr-toolbar">
                    <div className="rdp-cr-search-wrap">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" strokeWidth="2"
                            strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <circle cx="11" cy="11" r="8" />
                            <line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                        <input
                            type="text"
                            className="rdp-cr-search"
                            placeholder="Search client name…"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                            <button type="button" className="rdp-cr-search-clear" onClick={() => setSearchTerm("")} aria-label="Clear search">
                                ✕
                            </button>
                        )}
                    </div>
                    <button type="button" className="rdp-cr-toggle-all" onClick={allExpanded ? collapseAll : expandAll}>
                        {allExpanded ? "Collapse All" : "Expand All"}
                    </button>
                </div>
            )}

            {/* ── Grouped feature cards ───────────────────────────── */}
            {showGrouped && totalCount > 0 && (
                <div className="rdp-cr-features">
                    {safeFeatures.map((feature) => {
                        const fId = feature.featureId;
                        const clients = featureClientMap[fId] || [];
                        const filteredClients = filterClients(clients);
                        const isExpanded = !!expandedFeatures[fId];
                        const isLoadingF = !!featureLoading[fId];
                        const errorF = featureErrors[fId];

                        return (
                            <div key={fId} className={`rdp-cr-feature ${isExpanded ? "rdp-cr-feature--expanded" : ""}`}>
                                {/* Feature header — clickable to expand/collapse */}
                                <button
                                    type="button"
                                    className="rdp-cr-feature__header"
                                    onClick={() => toggleExpand(fId)}
                                    aria-expanded={isExpanded}
                                >
                                    <div className="rdp-cr-feature__info">
                                        <svg
                                            className={`rdp-cr-feature__chevron ${isExpanded ? "rdp-cr-feature__chevron--open" : ""}`}
                                            width="16" height="16" viewBox="0 0 24 24" fill="none"
                                            stroke="currentColor" strokeWidth="2.5"
                                            strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                            <polyline points="9 18 15 12 9 6" />
                                        </svg>
                                        <span className="rdp-cr-feature__name">
                                            {feature.featureName || fId}
                                        </span>
                                        <code className="rdp-cr-feature__id">{fId}</code>
                                    </div>
                                    <div className="rdp-cr-feature__badges">
                                        {isLoadingF ? (
                                            <span className="rdp-cr-feature__loading">Loading…</span>
                                        ) : errorF ? (
                                            <span className="rdp-cr-feature__error-badge">Error</span>
                                        ) : (
                                            <span className="rdp-cr-feature__count">
                                                {clients.length} client{clients.length !== 1 ? "s" : ""}
                                            </span>
                                        )}
                                    </div>
                                </button>

                                {/* Expanded client list */}
                                {isExpanded && (
                                    <div className="rdp-cr-feature__body">
                                        {isLoadingF ? (
                                            <div className="rdp-clients-skeleton">
                                                {[1, 2, 3].map(i => <span key={i} className="rdp-client-chip rdp-client-chip--skeleton" />)}
                                            </div>
                                        ) : errorF ? (
                                            <p className="rdp-cr-feature__error-msg">{errorF}</p>
                                        ) : filteredClients.length === 0 ? (
                                            <p className="rdp-cr-feature__empty">
                                                {searchTerm ? "No clients match your search." : "No clients found for this feature."}
                                            </p>
                                        ) : (
                                            <div className="rdp-cr-client-grid">
                                                {filteredClients.map((c, idx) => (
                                                    <div key={`${c.clientId}-${idx}`} className="rdp-cr-client-card">
                                                        <span className="rdp-cr-client-card__id">{resolveClientDisplayName(c.clientId) || "Unknown"}</span>
                                                        {c.status && (
                                                            <span className={`rdp-cr-client-card__status rdp-cr-client-card__status--${(c.status || "").toLowerCase()}`}>
                                                                {c.status}
                                                            </span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* ── Fallback flat view (no features loaded) ─────────── */}
            {!showGrouped && totalClientIds.length > 0 && (
                <div className="rdp-clients-list">
                    {totalClientIds.map((id) => (
                        <span key={id} className="rdp-client-chip">{resolveClientDisplayName(id)}</span>
                    ))}
                </div>
            )}

            {totalCount === 0 && (
                <div className="rdp-empty-state rdp-empty-state--sm">
                    <p>No clients recorded for this release.</p>
                </div>
            )}
        </div>
    );
}

/* ─── Main Panel ───────────────────────────────────────────── */

/**
 * ReleaseDetailPanel
 * @param {object}   release          - the release object from the table row click
 * @param {function} onBack           - clears selection, returns to table
 * @param {function} onReleaseUpdated - tells parent to refetch list
 */
const ReleaseDetailPanel = ({ release, onBack, onReleaseUpdated }) => {
    const [localRelease, setLocalRelease] = useState(release);
    const [showAddFeature, setShowAddFeature] = useState(false);
    const [showExecute, setShowExecute] = useState(false);
    const [markingReady, setMarkingReady] = useState(false);
    const [markReadyError, setMarkReadyError] = useState("");
    const [actionSuccess, setActionSuccess] = useState("");

    // ── Delete state ──────────────────────────────────────────── */
    const [deletingRelease, setDeletingRelease] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [removingFeatureId, setRemovingFeatureId] = useState(null);

    // ── Features state (API 11) ──────────────────────────────── */
    const [features, setFeatures] = useState([]);
    const [featuresCount, setFeaturesCount] = useState(0);
    const [detailLoading, setDetailLoading] = useState(true);
    const [detailError, setDetailError] = useState("");

    const status = (localRelease.status || "").toUpperCase();
    const canModifyFeatures = status === "DRAFT" || status === "READY";

    /* ── Fetch fresh release + features in parallel (API 10 + 11) */
    const loadReleaseDetail = useCallback(async () => {
        const rid = release?.releaseId;
        if (!rid) return;

        setDetailLoading(true);
        setDetailError("");
        try {
            const [releaseRes, featuresRes] = await Promise.all([
                fetchRelease(rid),
                fetchReleaseFeatures(rid),
            ]);

            // Update release with fresh server data
            if (releaseRes?.data) {
                setLocalRelease(releaseRes.data);
            }

            // Set features list
            const featuresList = featuresRes?.data?.features || [];
            setFeatures(featuresList);
            setFeaturesCount(featuresRes?.data?.count ?? featuresList.length);
        } catch (err) {
            setDetailError(err?.message || "Failed to load release details.");
        } finally {
            setDetailLoading(false);
        }
    }, [release?.releaseId]);

    useEffect(() => {
        loadReleaseDetail();
    }, [loadReleaseDetail]);

    /* ── Mark as READY (API 4) ─────────────────────────────── */
    const handleMarkReady = async () => {
        setMarkingReady(true);
        setMarkReadyError("");
        try {
            const result = await updateReleaseStatus(localRelease.releaseId, "READY");
            const updated = result?.data || { ...localRelease, status: "READY" };
            setLocalRelease(updated);
            setActionSuccess("Release marked as READY! It can now be executed.");
            onReleaseUpdated?.();
        } catch (err) {
            setMarkReadyError(err?.message || "Failed to update status.");
        } finally {
            setMarkingReady(false);
        }
    };

    /* ── After execute (API 5) success ─────────────────────── */
    const handleExecuteSuccess = (result) => {
        const updated = result?.data?.release || { ...localRelease, status: "RELEASED" };
        setLocalRelease(updated);
        setActionSuccess(
            `Release executed! ${result?.data?.summary?.featuresProcessed ?? ""} features processed, ${result?.data?.summary?.clientReleaseItemsCreated ?? ""} client release items created.`
        );
        onReleaseUpdated?.();
    };

    /* ── After feature added (API 3) ───────────────────────── */
    const handleFeatureAdded = (msg, newFeature) => {
        setActionSuccess(msg || "Feature added successfully!");
        // Optimistically append to local features list
        if (newFeature) {
            setFeatures((prev) => [...prev, newFeature]);
            setFeaturesCount((prev) => prev + 1);
        } else {
            // Fallback: re-fetch features list
            loadReleaseDetail();
        }
    };

    /* ── Remove feature (API 13) ───────────────────────────── */
    const handleRemoveFeature = async (featureId) => {
        if (!window.confirm(`Remove feature "${featureId}" from this release?`)) return;

        // Snapshot before optimistic mutation
        const prevFeatures = features;
        const prevCount = featuresCount;

        setRemovingFeatureId(featureId);
        setMarkReadyError("");
        try {
            await deleteFeatureFromRelease(localRelease.releaseId, featureId);
            // Commit optimistic removal only on success
            setFeatures((prev) => prev.filter((f) => f.featureId !== featureId));
            setFeaturesCount((prev) => Math.max(0, prev - 1));
            setActionSuccess(`Feature "${featureId}" removed.`);
        } catch (err) {
            // Rollback to snapshot on failure
            setFeatures(prevFeatures);
            setFeaturesCount(prevCount);
            const msg = err?.status === 409
                ? "Cannot remove features from a RELEASED release."
                : err?.status === 404
                    ? "Feature not found in this release."
                    : err?.message || "Failed to remove feature.";
            setMarkReadyError(msg);
        } finally {
            setRemovingFeatureId(null);
        }
    };

    /* ── Delete release (API 12) ───────────────────────────── */
    const handleDeleteRelease = async () => {
        setShowDeleteConfirm(false);
        setDeletingRelease(true);
        setMarkReadyError("");
        try {
            await deleteRelease(localRelease.releaseId);
            setActionSuccess(`Release "${localRelease.releaseName}" deleted.`);
            onReleaseUpdated?.();
            // Navigate back after a brief moment so the user sees the success message
            setTimeout(() => onBack?.(), 600);
        } catch (err) {
            const msg = err?.status === 409
                ? "Cannot delete a RELEASED release."
                : err?.status === 404
                    ? "Release not found."
                    : err?.message || "Failed to delete release.";
            setMarkReadyError(msg);
        } finally {
            setDeletingRelease(false);
        }
    };

    /* ── Delete confirm dialog: keyboard accessibility ──────── */
    const deleteConfirmCancelRef = useRef(null);
    const deleteTriggerRef = useRef(null);

    useEffect(() => {
        if (showDeleteConfirm) {
            // Remember the element that opened the dialog so we can restore focus
            deleteTriggerRef.current = document.activeElement;
            // Move focus into the dialog (Cancel button)
            requestAnimationFrame(() => deleteConfirmCancelRef.current?.focus());

            const handleKeyDown = (e) => {
                if (e.key === "Escape") {
                    setShowDeleteConfirm(false);
                    return;
                }
                // Focus trap: cycle Tab / Shift+Tab within the dialog
                if (e.key === "Tab" && deleteConfirmCancelRef.current) {
                    const dialog = deleteConfirmCancelRef.current.closest(".rdp-confirm-dialog");
                    if (!dialog) return;
                    const focusable = Array.from(
                        dialog.querySelectorAll(
                            "button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex=\"-1\"])"
                        )
                    );
                    if (focusable.length === 0) return;
                    const first = focusable[0];
                    const last = focusable[focusable.length - 1];
                    if (e.shiftKey) {
                        if (document.activeElement === first) {
                            e.preventDefault();
                            last.focus();
                        }
                    } else {
                        if (document.activeElement === last) {
                            e.preventDefault();
                            first.focus();
                        }
                    }
                }
            };
            document.addEventListener("keydown", handleKeyDown);
            return () => {
                document.removeEventListener("keydown", handleKeyDown);
            };
        } else {
            // Restore focus to the trigger element when dialog closes
            deleteTriggerRef.current?.focus();
        }
    }, [showDeleteConfirm]);

    /* ── Auto-dismiss success banner after 4 s ───────────────── */
    const successTimerRef = useRef(null);
    useEffect(() => {
        if (!actionSuccess) return;
        clearTimeout(successTimerRef.current);
        successTimerRef.current = setTimeout(() => setActionSuccess(""), 4000);
        return () => clearTimeout(successTimerRef.current);
    }, [actionSuccess]);

    /* ── Auto-dismiss error banner after 6 s ──────────────────── */
    const errorTimerRef = useRef(null);
    useEffect(() => {
        if (!markReadyError) return;
        clearTimeout(errorTimerRef.current);
        errorTimerRef.current = setTimeout(() => setMarkReadyError(""), 6000);
        return () => clearTimeout(errorTimerRef.current);
    }, [markReadyError]);

    return (
        <div className="rdp" role="region" aria-label="Release detail">

            {/* ── Breadcrumb / back ──────────────────────────────── */}
            <div className="rdp-breadcrumb">
                <button
                    type="button"
                    className="rdp-back-btn"
                    onClick={() => onBack?.()}
                    aria-label="Back to releases list"
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2.5"
                        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="m15 18-6-6 6-6" />
                    </svg>
                    Back to All Releases
                </button>
            </div>

            {/* ── Release header card ───────────────────────────── */}
            <div className="rdp-header">
                <div className="rdp-header__inner">
                    <div className="rdp-header__title-row">
                        <h2 className="rdp-header__title">{localRelease.releaseName || "Unnamed Release"}</h2>
                        <StatusBadge status={localRelease.status} />
                    </div>
                    <div className="rdp-header__meta-grid">
                        <div className="rdp-meta-block">
                            <span className="rdp-meta-block__icon" aria-hidden="true">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                    <circle cx="12" cy="7" r="4" />
                                </svg>
                            </span>
                            <div className="rdp-meta-block__content">
                                <span className="rdp-meta-block__label">Created by</span>
                                <span className="rdp-meta-block__value">{localRelease.createdBy || "—"}</span>
                            </div>
                        </div>
                        <div className="rdp-meta-block">
                            <span className="rdp-meta-block__icon" aria-hidden="true">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                    <line x1="16" y1="2" x2="16" y2="6" />
                                    <line x1="8" y1="2" x2="8" y2="6" />
                                    <line x1="3" y1="10" x2="21" y2="10" />
                                </svg>
                            </span>
                            <div className="rdp-meta-block__content">
                                <span className="rdp-meta-block__label">Created</span>
                                <span className="rdp-meta-block__value">{formatDateTime(localRelease.createdAt)}</span>
                            </div>
                        </div>
                        <div className="rdp-meta-block">
                            <span className="rdp-meta-block__icon" aria-hidden="true">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10" />
                                    <polyline points="12 6 12 12 16 14" />
                                </svg>
                            </span>
                            <div className="rdp-meta-block__content">
                                <span className="rdp-meta-block__label">Updated</span>
                                <span className="rdp-meta-block__value">{formatDateTime(localRelease.updatedAt)}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Status journey ──────────────────────────────────── */}
            <StatusJourney status={localRelease.status} />

            {/* ── Detail loading state ─────────────────────────────── */}
            {detailLoading && (
                <div className="rdp-detail-loading">
                    <div className="rdp-detail-loading__bar" />
                    <span className="rdp-detail-loading__text">Loading release details…</span>
                </div>
            )}

            {/* ── Detail error ─────────────────────────────────────── */}
            {detailError && (
                <div className="rdp-error-banner" role="alert">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2"
                        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    {detailError}
                </div>
            )}
            {/* ── Success banner ──────────────────────────────────── */}
            {actionSuccess && (
                <div className="rdp-success-banner" role="status">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2.5"
                        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                        <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                    {actionSuccess}
                </div>
            )}

            {/* ── Mark Ready error ───────────────────────────────── */}
            {markReadyError && (
                <div className="rdp-error-banner" role="alert">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2"
                        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    {markReadyError}
                </div>
            )}

            {/* ── Features Table (API 11) ─────────────────────────── */}
            {!detailLoading && (
                <div className="rdp-features-section">
                    <div className="rdp-features-section__header">
                        <div className="rdp-features-section__title-row">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2"
                                strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                <line x1="3" y1="9" x2="21" y2="9" />
                                <line x1="9" y1="21" x2="9" y2="9" />
                            </svg>
                            <h3 className="rdp-features-section__title">
                                Features
                                <span className="rdp-features-section__count">{features.length}</span>
                            </h3>
                        </div>
                        {status === "DRAFT" && (
                            <button
                                type="button"
                                className="rdp-btn rdp-btn--sm rdp-btn--outline"
                                onClick={() => setShowAddFeature(true)}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                    stroke="currentColor" strokeWidth="2.5"
                                    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <line x1="12" y1="5" x2="12" y2="19" />
                                    <line x1="5" y1="12" x2="19" y2="12" />
                                </svg>
                                Add Feature
                            </button>
                        )}
                    </div>

                    {/* Status info banner */}
                    {status === "RELEASED" && features.length > 0 && (
                        <div className="rdp-features-info-banner">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2"
                                strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="16" x2="12" y2="12" />
                                <line x1="12" y1="8" x2="12.01" y2="8" />
                            </svg>
                            This release has been published. Features cannot be modified.
                        </div>
                    )}
                    {status === "READY" && features.length > 0 && (
                        <div className="rdp-features-info-banner rdp-features-info-banner--ready">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2"
                                strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="16" x2="12" y2="12" />
                                <line x1="12" y1="8" x2="12.01" y2="8" />
                            </svg>
                            Release is READY. You can remove features or execute. Adding new features requires reverting to DRAFT (not available).
                        </div>
                    )}

                    {features.length > 0 ? (
                        <div className="rdp-features-table-wrap">
                            <table className="rdp-features-table">
                                <thead>
                                    <tr>
                                        <th>Feature ID</th>
                                        <th>Name</th>
                                        <th>Version</th>
                                        <th>Added</th>
                                        {canModifyFeatures && <th className="rdp-features-table__actions-col">Actions</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {features.map((f) => (
                                        <tr key={f.featureId} className={removingFeatureId === f.featureId ? "rdp-features-table__row--removing" : ""}>
                                            <td>
                                                <code className="rdp-feature-id">{f.featureId}</code>
                                            </td>
                                            <td>{f.featureName || "—"}</td>
                                            <td><span className="rdp-feature-version">{f.version || "1.0.0"}</span></td>
                                            <td className="rdp-features-table__date">{formatDateTime(f.createdAt)}</td>
                                            {canModifyFeatures && (
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="rdp-btn rdp-btn--sm rdp-btn--danger"
                                                        disabled={removingFeatureId === f.featureId}
                                                        onClick={() => handleRemoveFeature(f.featureId)}
                                                        title={`Remove ${f.featureId}`}
                                                    >
                                                        {removingFeatureId === f.featureId ? (
                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                                                stroke="currentColor" strokeWidth="2"
                                                                strokeLinecap="round" strokeLinejoin="round"
                                                                aria-hidden="true"
                                                                style={{ animation: "rdp-spin 1s linear infinite" }}>
                                                                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                                                            </svg>
                                                        ) : (
                                                            <>
                                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                                                    stroke="currentColor" strokeWidth="2"
                                                                    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                                    <polyline points="3 6 5 6 21 6" />
                                                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                                                </svg>
                                                                Remove
                                                            </>
                                                        )}
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="rdp-features-empty">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="1.5"
                                strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                <line x1="12" y1="8" x2="12" y2="16" />
                                <line x1="8" y1="12" x2="16" y2="12" />
                            </svg>
                            <p>No features added yet.</p>
                            {status === "DRAFT" && (
                                <button
                                    type="button"
                                    className="rdp-btn rdp-btn--outline rdp-btn--sm"
                                    onClick={() => setShowAddFeature(true)}
                                >
                                    Add your first feature
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* ── Action toolbar (status-dependent) ─────────────── */}
            {(status === "DRAFT" || status === "READY") && (
                <div className="rdp-action-toolbar">
                    {/* Left slot: always show Delete as a quiet ghost button */}
                    <div className="rdp-action-toolbar__left">
                        <button
                            type="button"
                            className="rdp-toolbar-btn rdp-toolbar-btn--ghost-danger"
                            onClick={() => setShowDeleteConfirm(true)}
                            disabled={deletingRelease}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2"
                                strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            </svg>
                            Delete Release
                        </button>
                    </div>

                    {/* Right slot: DRAFT → Mark as Ready (only if features exist) | READY → Execute */}
                    <div className="rdp-action-toolbar__right">
                        {status === "DRAFT" && features.length === 0 && (
                            <div className="rdp-action-toolbar__hint">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                                    stroke="currentColor" strokeWidth="2"
                                    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <circle cx="12" cy="12" r="10" />
                                    <line x1="12" y1="16" x2="12" y2="12" />
                                    <line x1="12" y1="8" x2="12.01" y2="8" />
                                </svg>
                                Add at least one feature to mark as Ready
                            </div>
                        )}

                        {status === "DRAFT" && features.length > 0 && (
                            <button
                                type="button"
                                className="rdp-toolbar-btn rdp-toolbar-btn--primary"
                                onClick={handleMarkReady}
                                disabled={markingReady}
                            >
                                {markingReady ? (
                                    <>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                                            stroke="currentColor" strokeWidth="2"
                                            strokeLinecap="round" strokeLinejoin="round"
                                            aria-hidden="true"
                                            style={{ animation: "rdp-spin 1s linear infinite" }}>
                                            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                                        </svg>
                                        Processing…
                                    </>
                                ) : (
                                    <>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                                            stroke="currentColor" strokeWidth="2.5"
                                            strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                            <polyline points="20 6 9 17 4 12" />
                                        </svg>
                                        Mark as Ready
                                    </>
                                )}
                            </button>
                        )}

                        {status === "READY" && (
                            <button
                                type="button"
                                className="rdp-toolbar-btn rdp-toolbar-btn--execute"
                                onClick={() => setShowExecute(true)}
                            >
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                                    stroke="currentColor" strokeWidth="2.5"
                                    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <polygon points="5 3 19 12 5 21 5 3" />
                                </svg>
                                Execute Release
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* RELEASED → show Clients Reached (API 6 + API 8 per feature) */}
            {status === "RELEASED" && (
                <ClientsReached releaseId={localRelease.releaseId} features={features} />
            )}

            {/* ── Modals ─────────────────────────────────────────── */}
            <AddFeatureModal
                show={showAddFeature}
                onHide={() => setShowAddFeature(false)}
                release={localRelease}
                onSuccess={handleFeatureAdded}
            />

            <ExecuteReleaseModal
                show={showExecute}
                onHide={() => setShowExecute(false)}
                release={localRelease}
                features={features}
                onSuccess={handleExecuteSuccess}
            />

            {/* ── Delete Confirmation Dialog ───────────────────────── */}
            {showDeleteConfirm && (
                <div className="rdp-confirm-overlay" onClick={() => setShowDeleteConfirm(false)}>
                    <div className="rdp-confirm-dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="rdp-confirm-dialog__icon">
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2"
                                strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="8" x2="12" y2="12" />
                                <line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                        </div>
                        <h3 className="rdp-confirm-dialog__title">Delete Release</h3>
                        <p className="rdp-confirm-dialog__msg">
                            Are you sure you want to delete <strong>"{localRelease.releaseName}"</strong>?
                            This will remove all features in this release. This action cannot be undone.
                        </p>
                        <div className="rdp-confirm-dialog__actions">
                            <button
                                ref={deleteConfirmCancelRef}
                                type="button"
                                className="rdp-btn rdp-btn--outline"
                                onClick={() => setShowDeleteConfirm(false)}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="rdp-btn rdp-btn--danger"
                                onClick={handleDeleteRelease}
                                disabled={deletingRelease}
                            >
                                {deletingRelease ? (
                                    <>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                            stroke="currentColor" strokeWidth="2"
                                            strokeLinecap="round" strokeLinejoin="round"
                                            aria-hidden="true"
                                            style={{ animation: "rdp-spin 1s linear infinite" }}>
                                            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                                        </svg>
                                        Deleting…
                                    </>
                                ) : (
                                    "Yes, Delete Release"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReleaseDetailPanel;
